import StarRating from '../../../../components/StarRating';
import { mapToOption, formatDateCST, formatTimeCST } from '../../../../utlis';
import { timezoneOptions, formatTimeInput } from '../salesInterviewUtils';

const SalesInterviewsForm = ({
  register,
  errors,
  handleSubmit,
  onSubmit,
  closeModal,
  setValue,
  watch,
  isSubmitting,
  refCandidate,
  refCandidateLoading,
  refVendor,
  refVendorLoading,
  refTechnologies,
  refTechnologiesLoading,
  jobs,
  selectedJob,
  onJobChange,
}) => {
  const interviewStatus = watch('interviewStatus');

  // Handle time input change with auto-formatting
  const handleTimeInputChange = (e) => {
    const formatted = formatTimeInput(e.target.value);
    setValue('interviewTime', formatted);
  };

  const onRatingChange = (rating) => {
    setValue('rating', parseInt(rating));
  };

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
      <div className="grid grid-cols-2 gap-5">
        <div className="flex flex-col w-72">
          <label htmlFor="candidateName" className="text-text-hint mb-1">
            Candidate Name <span className="text-danger-500">*</span>
          </label>
          <select
            {...register('candidateName', { required: 'Candidate Name is required' })}
            className="outline-none border-2 border-secondary-500 rounded-lg"
          >
            {mapToOption(
              !refCandidateLoading ? refCandidate?.data?.records : [],
              'first_name',
              'candidate_id'
            ).map((option) => (
              <option key={option.value} value={option.value}>
                {option.text}
              </option>
            ))}
          </select>
          {errors.candidateName && <p className="text-red-500">{errors.candidateName.message}</p>}
        </div>
        <div className="flex flex-col w-72">
          <label htmlFor="job" className="text-text-hint mb-1">
            Job <span className="text-danger-500">*</span>
          </label>
          <select
            {...register('job', { required: 'Job is required', valueAsNumber: true })}
            id="job"
            className="outline-none border-2 border-secondary-500 rounded-lg"
            value={selectedJob}
            onChange={onJobChange}
          >
            <option value="">Select a job</option>
            {jobs.map((job) => (
              <option key={job.job_id} value={job.job_id}>
                {job.job_title}
              </option>
            ))}
          </select>
          {errors.job && <p className="text-red-500">{errors.job.message}</p>}
        </div>
        <div className="flex flex-col w-72">
          <label htmlFor="jobTitle" className="text-text-hint mb-1">
            Job Title
          </label>
          <input
            {...register('jobTitle', { required: 'Job Title is required' })}
            placeholder="Job Title"
            id="jobTitle"
            type="text"
            className="outline-none border-2 border-secondary-500 rounded-lg"
          />
          {errors.jobTitle && <p className="text-red-500">{errors.jobTitle.message}</p>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-5">
        <div className="flex flex-col w-72">
          <label htmlFor="technology" className="text-text-hint mb-1">
            Technology <span className="text-danger-500">*</span>
          </label>
          <select
            {...register('technology', { required: 'Technology is required' })}
            className="outline-none border-2 border-secondary-500 rounded-lg"
          >
            {!refTechnologiesLoading &&
              mapToOption(refTechnologies?.data?.records, 'technology_name', 'technology_id').map(
                (option, i) => (
                  <option key={`${option.value}_${i + 1}`} value={option.value}>
                    {option.text}
                  </option>
                )
              )}
          </select>
          {errors.technology && <p className="text-red-500">{errors.technology.message}</p>}
        </div>
        <div className="flex flex-col w-72">
          <label htmlFor="vendor" className="text-text-hint mb-1">
            Vendor <span className="text-danger-500">*</span>
          </label>
          <select
            {...register('vendor', { required: 'Vendor is required' })}
            className="outline-none border-2 border-secondary-500 rounded-lg"
          >
            {!refVendorLoading &&
              mapToOption(refVendor.response?.data, 'name', 'vendor_id').map((option) => (
                <option key={option.value} value={option.value}>
                  {option.text}
                </option>
              ))}
          </select>
          {errors.vendor && <p className="text-red-500">{errors.vendor.message}</p>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-5">
        <div className="flex flex-col w-72">
          <label htmlFor="clientCompany" className="text-text-hint mb-1">
            Client Name <span className="text-danger-500">*</span>
          </label>
          <input
            {...register('clientCompany', { required: 'Client Name is required' })}
            id="clientCompany"
            type="text"
            className="outline-none border-2 border-secondary-500 rounded-lg"
          />
          {errors.clientCompany && <p className="text-red-500">{errors.clientCompany.message}</p>}
        </div>
        <div className="flex flex-col w-72">
          <label htmlFor="clientName" className="text-text-hint mb-1">
            Sales Person <span className="text-danger-500">*</span>
          </label>
          <input
            {...register('clientName', { required: 'Sales Person is required' })}
            id="clientName"
            type="text"
            className="outline-none border-2 border-secondary-500 rounded-lg"
          />
          {errors.clientName && <p className="text-red-500">{errors.clientName.message}</p>}
        </div>
      </div>

      {/* Date and Time */}
      <div className="flex gap-4 flex-wrap">
        <div className="flex flex-col w-40">
          <label htmlFor="interViewDate" className="text-text-hint mb-1 text-sm">
            Interview Date <span className="text-danger-500">*</span>
          </label>
          <input
            {...register('interviewDate', { required: 'Interview Date is required' })}
            id="interViewDate"
            type="date"
            className="outline-none border-2 border-secondary-500 rounded-lg px-2 py-1.5 text-sm"
          />
          {errors.interviewDate && <p className="text-red-500 text-xs">{errors.interviewDate.message}</p>}
        </div>
        <div className="flex flex-col w-24">
          <label htmlFor="interviewTime" className="text-text-hint mb-1 text-sm">
            Time <span className="text-danger-500">*</span>
          </label>
          <input
            {...register('interviewTime', {
              required: 'Time is required',
              pattern: { value: /^(0?[1-9]|1[0-2]):[0-5][0-9]$/, message: 'HH:MM' },
            })}
            id="interviewTime"
            type="text"
            placeholder="HH:MM"
            maxLength={5}
            onChange={handleTimeInputChange}
            className="outline-none border-2 border-secondary-500 rounded-lg px-2 py-1.5 text-sm"
          />
          {errors.interviewTime && <p className="text-red-500 text-xs">{errors.interviewTime.message}</p>}
        </div>
        <div className="flex flex-col w-20">
          <label htmlFor="interviewTimeAmPm" className="text-text-hint mb-1 text-sm">
            AM/PM <span className="text-danger-500">*</span>
          </label>
          <select
            {...register('interviewTimeAmPm', { required: 'Required' })}
            id="interviewTimeAmPm"
            className="outline-none border-2 border-secondary-500 rounded-lg px-2 py-1.5 text-sm"
          >
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
          {errors.interviewTimeAmPm && (
            <p className="text-red-500 text-xs">{errors.interviewTimeAmPm.message}</p>
          )}
        </div>
        <div className="flex flex-col w-36">
          <label htmlFor="interviewTimezone" className="text-text-hint mb-1 text-sm">
            Timezone <span className="text-danger-500">*</span>
          </label>
          <select
            {...register('interviewTimezone', { required: 'Timezone is required' })}
            id="interviewTimezone"
            className="outline-none border-2 border-secondary-500 rounded-lg px-2 py-1.5 text-sm"
          >
            {timezoneOptions.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
          {errors.interviewTimezone && (
            <p className="text-red-500 text-xs">{errors.interviewTimezone.message}</p>
          )}
        </div>
      </div>

      {/* Interviewers */}
      <div className="grid grid-cols-2 gap-5">
        <div className="flex flex-col w-72">
          <label htmlFor="interviewer1" className="text-text-hint mb-1">
            Interviewer 1 <span className="text-danger-500">*</span>
          </label>
          <input
            {...register('interviewer1', { required: 'Interviewer 1 is required' })}
            id="interviewer1"
            type="text"
            className="outline-none border-2 border-secondary-500 rounded-lg"
          />
          {errors.interviewer1 && <p className="text-red-500">{errors.interviewer1.message}</p>}
        </div>
        <div className="flex flex-col w-72">
          <label htmlFor="interviewer2" className="text-text-hint mb-1">
            Interviewer 2
          </label>
          <input
            {...register('interviewer2')}
            id="interviewer2"
            type="text"
            className="outline-none border-2 border-secondary-500 rounded-lg"
          />
        </div>
      </div>

      {/* Links */}
      <div className="grid grid-cols-2 gap-5">
        <div className="flex flex-col w-72">
          <label htmlFor="link" className="text-text-hint mb-1">
            Appointment Link <span className="text-danger-500">*</span>
          </label>
          <input
            {...register('link', { required: 'Appointment Link is required' })}
            id="link"
            type="text"
            className="outline-none border-2 border-secondary-500 rounded-lg"
          />
          {errors.link && <p className="text-red-500">{errors.link.message}</p>}
        </div>
        <div className="flex flex-col w-72">
          <label htmlFor="appointmentDetails" className="text-text-hint mb-1">
            Appointment Details <span className="text-danger-500">*</span>
          </label>
          <input
            {...register('appointmentDetails', { required: 'Appointment Details is required' })}
            id="appointmentDetails"
            type="text"
            className="outline-none border-2 border-secondary-500 rounded-lg"
          />
          {errors.appointmentDetails && (
            <p className="text-red-500">{errors.appointmentDetails.message}</p>
          )}
        </div>
      </div>

      {/* Interview Status */}
      <div>
        <div className="w-96">
          <label className="text-text-hint" htmlFor="interviewStatus">
            Interview Status <span className="text-danger-500">*</span>
          </label>
          <ul className="grid grid-cols-3 mt-1">
            {['In Progress', 'Shortlisted', 'Not Selected'].map((status) => (
              <li className="relative" key={status}>
                <input
                  {...register('interviewStatus', { required: 'Interview status is required' })}
                  type="radio"
                  value={status}
                  id={status}
                  className="sr-only peer"
                />
                <label
                  className={`flex justify-center items-center py-2 px-3 bg-white border border-secondary-800 cursor-pointer focus:outline-none hover:bg-gray-50 ${
                    interviewStatus === status ? '!bg-secondary-500 text-white border-secondary-800' : ''
                  } ${
                    status === 'In Progress'
                      ? 'rounded-tl-full rounded-bl-full'
                      : status === 'Not Selected'
                      ? 'rounded-tr-full rounded-br-full'
                      : ''
                  }`}
                  htmlFor={status}
                >
                  {status}
                </label>
              </li>
            ))}
          </ul>
          {errors.interviewStatus && (
            <span className="text-danger-500">{errors.interviewStatus.message}</span>
          )}
        </div>
      </div>

      {/* Rating */}
      <div>
        <h3>Rating</h3>
        <StarRating initialRating={parseInt(watch('rating')) || 0} fontSize={24} onRatingChange={onRatingChange} />
      </div>

      {/* Feedback */}
      <div className="grid grid-cols-2 gap-5">
        <div className="flex flex-col col-span-3">
          <label htmlFor="feedback" className="text-text-hint mb-1">
            Feedback
          </label>
          <textarea
            {...register('feedback')}
            id="feedback"
            placeholder="feedback"
            className="focus:outline-none border rounded-lg"
            rows={4}
          />
        </div>
      </div>

      {/* Display-only audit fields for Edit Mode */}
      {!!watch('interviewId') && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-1.5 md:gap-2 mt-4 pt-4 border-t border-gray-200">
          <div className="flex flex-col space-y-1">
            <label className="text-text-hint text-xs">Created By</label>
            <p className="text-sm text-gray-600">{watch('created_by') || 'N/A'}</p>
          </div>
          <div className="flex flex-col space-y-1">
            <label className="text-text-hint text-xs">Date Created</label>
            <p className="text-sm text-gray-600">
              {watch('created') ? formatDateCST(watch('created')) : 'N/A'}
            </p>
            {watch('created') && (
              <p className="text-xs text-gray-500">{formatTimeCST(watch('created'))}</p>
            )}
          </div>
          <div className="flex flex-col space-y-1">
            <label className="text-text-hint text-xs">Updated By</label>
            <p className="text-sm text-gray-600">{watch('updated_by') || 'N/A'}</p>
          </div>
          <div className="flex flex-col space-y-1">
            <label className="text-text-hint text-xs">Last Updated</label>
            <p className="text-sm text-gray-600">
              {watch('updated') ? formatDateCST(watch('updated')) : 'N/A'}
            </p>
            {watch('updated') && (
              <p className="text-xs text-gray-500">{formatTimeCST(watch('updated'))}</p>
            )}
          </div>
        </div>
      )}

      {/* Buttons */}
      <div className="flex justify-between p-6 mt-6">
        <button
          type="button"
          onClick={closeModal}
          disabled={isSubmitting}
          className="border border-secondary-800 text-secondary-800 py-2 px-5 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Close
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="bg-secondary-700 text-text-light py-2 px-5 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  );
};

export default SalesInterviewsForm;
